import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import clsx from "clsx";
import { apiService, extractApiError } from "../services/api";
import type { RoadmapVotes } from "../types";

interface RoadmapVotingProps {
  itemId: string;
  disabled: boolean;
}

export default function RoadmapVoting({
  itemId,
  disabled,
}: RoadmapVotingProps) {
  const client = useQueryClient();
  const key = ["roadmap-votes", itemId];
  const votes = useQuery(key, async () => {
    const { data } = await apiService.getRoadmapVotes(itemId);
    if (!data.success || !data.data)
      throw new Error(data.error || "Failed to load project votes");
    return data.data;
  });
  const vote = useMutation(
    async (value: RoadmapVotes["myVote"]) => {
      const { data } = await apiService.setRoadmapVote(itemId, value);
      if (!data.success || !data.data)
        throw new Error(data.error || "Failed to update project vote");
      return data.data;
    },
    {
      onSuccess: (data) => {
        client.setQueryData(key, data);
        toast.success("Project vote updated");
      },
      onError: (error) => {
        toast.error(extractApiError(error));
      },
    },
  );
  if (votes.isError)
    return (
      <div role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
        Unable to load project votes.{" "}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => votes.refetch()}
          disabled={disabled}
        >
          Retry votes
        </button>
      </div>
    );
  if (!votes.data)
    return (
      <p
        role="status"
        className="mt-3 text-sm text-gray-500 dark:text-gray-400"
      >
        Loading votes...
      </p>
    );
  const current = votes.data;
  return (
    <div role="group" aria-label="Project votes" className="mt-3 flex gap-2">
      {([1, -1] as const).map((value) => {
        const selected = current.myVote === value;
        const Icon = value === 1 ? ThumbsUp : ThumbsDown;
        return (
          <button
            key={value}
            type="button"
            aria-label={value === 1 ? "Upvote" : "Downvote"}
            aria-pressed={selected}
            disabled={disabled || vote.isLoading || votes.isFetching}
            className={clsx(
              "btn gap-2",
              selected ? "btn-primary" : "btn-outline",
            )}
            onClick={() => vote.mutate(selected ? 0 : value)}
          >
            <Icon className="h-4 w-4" />
            {value === 1 ? current.upvotes : current.downvotes}
          </button>
        );
      })}
    </div>
  );
}
